import React, { useState, useEffect } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, X, Check, AlertTriangle, RotateCcw, Eye, EyeOff, Printer, ChevronUp, ChevronDown } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { ColorBadge } from "@/components/ColorBadge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useColorSettings } from '@/contexts/ColorSettingsContext';

export function PrepareOrderForm({ orderId, onClose, onUpdated }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [notes, setNotes] = useState({});
  const [error, setError] = useState(null);
  const [stockAvailable, setStockAvailable] = useState({});
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [codeFilter, setCodeFilter] = useState('');
  const { toast } = useToast();
  const { colorSettings } = useColorSettings();

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setError(null);
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          number,
          date,
          status,
          preparation_status,
          total,
          customer:customers(
            id,
            name
          ),
          order_items(
            id,
            quantity,
            pending_quantity,
            delivered_quantity,
            preparation_quantity,
            preparation_status,
            price,
            product:products(
              id,
              code,
              description,
              images,
              main_image_index,
              inventory(
                id,
                quantity,
                size,
                color
              )
            ),
            size,
            color
          )
        `)
        .eq('id', orderId)
        .single();

      if (error) throw error;

      // Inicializar las cantidades a preparar con 0
      const initialQuantities = {};
      const initialNotes = {};
      const initialStock = {};
      
      data.order_items.forEach(item => {
        initialQuantities[item.id] = 0; // Siempre inicializar en 0
        initialNotes[item.id] = '';
        
        // Calcular stock disponible
        const inventoryItem = item.product.inventory.find(
          inv => inv.size === item.size && inv.color === item.color
        );
        initialStock[item.id] = inventoryItem?.quantity || 0;
      });
      
      setQuantities(initialQuantities);
      setNotes(initialNotes);
      setStockAvailable(initialStock);
      setOrder(data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setError("No se pudieron cargar los detalles del pedido");
      toast({
        title: "Error",
        description: "No se pudieron cargar los detalles del pedido",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetInputs = () => {
    // Poner a cero todas las cantidades y limpiar notas sin tocar la BD
    const resetQuantities = {};
    const resetNotes = {};
    (order?.order_items || []).forEach(item => {
      resetQuantities[item.id] = 0;
      resetNotes[item.id] = '';
    });
    setQuantities(resetQuantities);
    setNotes(resetNotes);
    toast({ title: "Campos reiniciados", description: "Se han limpiado las cantidades y notas a preparar" });
  };

  const handleResetPreparedAll = async () => {
    try {
      if (!order) return;
      const itemsWithPreparation = order.order_items.filter(i => (i.preparation_quantity || 0) > 0);
      if (itemsWithPreparation.length === 0) {
        toast({ title: "Nada que resetear", description: "No hay líneas ya preparadas" });
        return;
      }

      if (!window.confirm("¿Seguro que quieres resetear TODAS las cantidades ya preparadas?")) {
        return;
      }

      setPreparing(true);
      setError(null);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Actualización única por order_id (más eficiente que pasar todos los IDs)
      const { error: updError } = await supabase
        .from('order_items')
        .update({
          preparation_quantity: 0,
          preparation_status: 'pending',
          preparation_date: null,
          preparation_user_id: null
        })
        .eq('order_id', orderId)
        .gt('preparation_quantity', 0);

      if (updError) throw updError;

      // Registrar historial en bloque
      const historyRows = itemsWithPreparation.map(i => ({
        order_id: orderId,
        order_item_id: i.id,
        quantity: 0,
        user_id: user.id,
        status: 'unprepared',
        notes: 'Reseteo masivo de preparación'
      }));

      const { error: histError } = await supabase
        .from('order_preparation_history')
        .insert(historyRows);

      if (histError) throw histError;

      // Volver a estado pendiente del pedido
      await supabase
        .from('orders')
        .update({ preparation_status: 'pending', preparation_date: null, preparation_user_id: null })
        .eq('id', orderId);

      // Refrescar datos y limpiar inputs
      await fetchOrderDetails();
      handleResetInputs();

      toast({ title: "Preparación reseteada", description: "Se ha eliminado la preparación de todas las líneas" });
      if (onUpdated) onUpdated();
    } catch (e) {
      console.error('Error resetting prepared all:', e);
      setError(e.message || 'No se pudo resetear la preparación');
      toast({ title: 'Error', description: e.message || 'No se pudo resetear la preparación', variant: 'destructive' });
    } finally {
      setPreparing(false);
    }
  };

  const handleQuantityChange = (itemId, value) => {
    const item = order.order_items.find(i => i.id === itemId);
    const pendingQty = item.pending_quantity;
    const stockQty = stockAvailable[itemId] || 0;
    const maxQty = Math.min(pendingQty, stockQty);
    const newValue = Math.min(Math.max(0, parseInt(value) || 0), maxQty);

    setQuantities(prev => ({
      ...prev,
      [itemId]: newValue
    }));
  };

  const handlePrepareAll = () => {
    const newQuantities = {};
    order.order_items.forEach(item => {
      // Calcular la cantidad realmente pendiente (cantidad total pendiente - cantidad ya preparada)
      const realPendingQuantity = Math.max(0, item.pending_quantity - item.preparation_quantity);
      
      // Solo procesar si hay cantidad realmente pendiente
      if (realPendingQuantity > 0) {
        const stockQty = stockAvailable[item.id] || 0;
        // Solo establecer la cantidad si hay stock disponible
        if (stockQty > 0) {
          newQuantities[item.id] = Math.min(realPendingQuantity, stockQty);
        }
      }
    });
    setQuantities(newQuantities);
  };

  const handlePrepareItem = (itemId) => {
    const item = order.order_items.find(i => i.id === itemId);
    if (item && item.pending_quantity > 0) {
      const stockQty = stockAvailable[itemId] || 0;
      setQuantities(prev => ({
        ...prev,
        [itemId]: Math.min(item.pending_quantity, stockQty)
      }));
    }
  };

  const handleNoteChange = (itemId, value) => {
    setNotes(prev => ({
      ...prev,
      [itemId]: value
    }));
  };

  const handleUnprepare = async (itemId) => {
    try {
      const item = order.order_items.find(i => i.id === itemId);
      if (!item || !item.preparation_quantity) return;

      setPreparing(true);
      setError(null);

      // Actualizar order_items
      const { error: itemError } = await supabase
        .from('order_items')
        .update({
          preparation_quantity: 0,
          preparation_status: 'pending',
          preparation_date: null,
          preparation_user_id: null
        })
        .eq('id', itemId);

      if (itemError) throw itemError;

      // Registrar en el historial
      const { error: historyError } = await supabase
        .from('order_preparation_history')
        .insert({
          order_id: orderId,
          order_item_id: itemId,
          quantity: 0,
          user_id: (await supabase.auth.getUser()).data.user.id,
          status: 'unprepared',
          notes: 'Preparación cancelada'
        });

      if (historyError) throw historyError;

      // Actualizar el estado local
      setQuantities(prev => ({
        ...prev,
        [itemId]: 0
      }));

      // Actualizar estado local sin refetch total
      setOrder(prev => ({
        ...prev,
        order_items: prev.order_items.map(i => i.id === itemId ? {
          ...i,
          preparation_quantity: 0,
          preparation_status: 'pending',
          preparation_date: null,
          preparation_user_id: null
        } : i)
      }));

      toast({
        title: "Éxito",
        description: "La línea se ha despreparado correctamente",
      });
    } catch (error) {
      console.error('Error unpreparing item:', error);
      setError(error.message || "No se pudo despreparar la línea");
      toast({
        title: "Error",
        description: error.message || "No se pudo despreparar la línea",
        variant: "destructive"
      });
    } finally {
      setPreparing(false);
    }
  };

  const handlePrepare = async () => {
    try {
      setError(null);
      setPreparing(true);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // Preparar filas a actualizar (solo las que cambian) y el historial en bloque
      const rowsToUpdate = [];
      const historyRows = [];

      for (const item of order.order_items) {
        const prevQty = item.preparation_quantity || 0;
        const quantityToPrepare = quantities[item.id] || 0;
        const status = quantityToPrepare > 0 ? 'prepared' : 'pending';

        // Solo enviar al servidor si hay un cambio efectivo
        if (quantityToPrepare !== prevQty || item.preparation_status !== status) {
          rowsToUpdate.push({
            id: item.id,
            preparation_quantity: quantityToPrepare,
            preparation_status: status,
            preparation_date: new Date().toISOString(),
            preparation_user_id: user.id
          });
        }

        if (quantityToPrepare > 0) {
          historyRows.push({
            order_id: orderId,
            order_item_id: item.id,
            quantity: quantityToPrepare,
            user_id: user.id,
            status: 'prepared',
            notes: notes[item.id]
          });
        }
      }

      // Si no hay cambios, avisar y salir pronto
      if (rowsToUpdate.length === 0) {
        toast({ title: 'Sin cambios', description: 'No hay modificaciones para guardar' });
        setPreparing(false);
        return;
      }

      // 1) Actualización en paralelo por lotes (seguro para NOT NULL y sin inserts accidentales)
      const chunkSize = 25;
      for (let i = 0; i < rowsToUpdate.length; i += chunkSize) {
        const chunk = rowsToUpdate.slice(i, i + chunkSize);
        const results = await Promise.all(
          chunk.map(row =>
            supabase
              .from('order_items')
              .update({
                preparation_quantity: row.preparation_quantity,
                preparation_status: row.preparation_status,
                preparation_date: row.preparation_date,
                preparation_user_id: row.preparation_user_id
              })
              .eq('id', row.id)
          )
        );
        const failed = results.find(r => r.error);
        if (failed && failed.error) throw failed.error;
      }

      // Actualizar estado local para reflejar cambios inmediatos
      setOrder(prev => ({
        ...prev,
        order_items: prev.order_items.map(i => {
          const row = rowsToUpdate.find(r => r.id === i.id);
          return row ? {
            ...i,
            preparation_quantity: row.preparation_quantity,
            preparation_status: row.preparation_status,
            preparation_date: row.preparation_date,
            preparation_user_id: row.preparation_user_id
          } : i;
        })
      }));

      // 2) Insert en bloque del historial (si aplica)
      if (historyRows.length > 0) {
        const { error: bulkHistoryError } = await supabase
          .from('order_preparation_history')
          .insert(historyRows);
        if (bulkHistoryError) throw bulkHistoryError;
      }

      // Verificar si todos los items están preparados
      const { data: updatedOrder, error: fetchError } = await supabase
        .from('orders')
        .select('order_items(preparation_status)')
        .eq('id', orderId)
        .single();

      if (fetchError) throw fetchError;

      const allItemsPrepared = updatedOrder.order_items.every(item => 
        item.preparation_status === 'prepared' || item.preparation_status === 'completed'
      );

      if (allItemsPrepared) {
        // Marcar el pedido como preparado
        const { error: orderError } = await supabase
          .from('orders')
          .update({ 
            preparation_status: 'prepared',
            preparation_date: new Date().toISOString(),
            preparation_user_id: user.id
          })
          .eq('id', orderId);

        if (orderError) throw orderError;
      }

      toast({
        title: "Éxito",
        description: "El pedido se ha preparado correctamente",
      });

      onClose();
      if (onUpdated) onUpdated();
    } catch (error) {
      console.error('Error preparing order:', error);
      setError(error.message || "No se pudo preparar el pedido");
      toast({
        title: "Error",
        description: error.message || "No se pudo preparar el pedido",
        variant: "destructive"
      });
    } finally {
      setPreparing(false);
    }
  };

  // Comparador de tallas: orden P, M, L, XL, 2XL, 3XL, 4XL, 5XL; soporta sinónimos (S≈P, XXL≈2XL, XXXL≈3XL) y numéricas
  const compareSizeStrings = (a, b) => {
    const normalize = (s) => (s || '').toString().trim().toLowerCase();
    const na = normalize(a);
    const nb = normalize(b);

    const rank = {
      'p': 1,
      's': 1,
      'm': 2,
      'l': 3,
      'xl': 4,
      '2xl': 5,
      'xxl': 5,
      '3xl': 6,
      'xxxl': 6,
      '4xl': 7,
      '5xl': 8
    };

    const ra = rank[na];
    const rb = rank[nb];
    if (ra !== undefined && rb !== undefined) return ra - rb;

    const extractFirstInt = (str) => {
      const m = (str || '').match(/\d+/);
      return m ? parseInt(m[0], 10) : null;
    };
    const ia = extractFirstInt(na);
    const ib = extractFirstInt(nb);
    if (ia !== null && ib !== null) return ia - ib;

    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    if (ia !== null) return -1;
    if (ib !== null) return 1;

    return na.localeCompare(nb, 'es', { numeric: true, sensitivity: 'base' });
  };

  const groupItemsByProduct = () => {
    if (!order) return [];
    
    const groupedItems = {};
    order.order_items.forEach(item => {
      // Si showOnlyPending está activo, solo incluir items con cantidad realmente pendiente
      const realPendingQuantity = Math.max(0, item.pending_quantity - item.preparation_quantity);
      if (showOnlyPending && realPendingQuantity === 0) return;

      const key = `${item.product.code}-${item.color}`;
      if (!groupedItems[key]) {
        groupedItems[key] = {
          productCode: item.product.code,
          productDescription: item.product.description,
          color: item.color,
          productImage: item.product.images?.[item.product.main_image_index || 0],
          items: []
        };
        

      }
      groupedItems[key].items.push(item);
    });
    
    // Construir lista de grupos con items
    let groups = Object.values(groupedItems).filter(group => group.items.length > 0);

    // Filtro por código de producto
    if (codeFilter && codeFilter.trim() !== '') {
      const q = codeFilter.trim().toLowerCase();
      groups = groups.filter(g => (g.productCode || '').toLowerCase().includes(q));
    }

    // Orden por código de producto y, en empate, por color
    groups.sort((a, b) => {
      const byCode = (a.productCode || '').localeCompare(b.productCode || '', 'es', { numeric: true, sensitivity: 'base' });
      if (byCode !== 0) return byCode;
      return (a.color || '').localeCompare(b.color || '', 'es', { numeric: true, sensitivity: 'base' });
    });

    // Ordenar tallas dentro de cada grupo
    groups.forEach(g => {
      g.items.sort((i1, i2) => compareSizeStrings(i1.size, i2.size));
    });

    return groups;
  };

  // Calcular totales para el resumen
  const calculateTotals = () => {
    if (!order) return { totalPending: 0, totalToPrepare: 0, totalPrepared: 0 };
    
    let totalPending = 0;
    let totalToPrepare = 0;
    let totalPrepared = 0;
    
    order.order_items.forEach(item => {
      // Cantidad realmente pendiente (pendiente - ya preparado)
      const realPendingQuantity = Math.max(0, item.pending_quantity - item.preparation_quantity);
      totalPending += realPendingQuantity;
      
      // Cantidad ya preparada
      totalPrepared += item.preparation_quantity || 0;
      
      // Cantidad a preparar (lo que se está preparando ahora)
      totalToPrepare += quantities[item.id] || 0;
    });
    
    return { totalPending, totalToPrepare, totalPrepared };
  };

  const handlePrint = () => {
    
    const printWindow = window.open('', '_blank');
    const printContent = `
      <html>
        <head>
          <title>Preparación de Pedido #${order.number}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
            
            * { box-sizing: border-box; }
            
            body { 
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background: #f8fafc;
              color: #1e293b;
              line-height: 1.6;
            }
            
            .print-container {
              max-width: 1200px;
              margin: 0 auto;
              background: white;
              border-radius: 12px;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
              overflow: hidden;
            }
            
            .header {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 30px;
              text-align: center;
            }
            
            .header h1 {
              margin: 0 0 10px 0;
              font-size: 2.5rem;
              font-weight: 700;
              letter-spacing: -0.025em;
            }
            
            .header-info {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 20px;
              margin-top: 20px;
            }
            
            .header-item {
              background: rgba(255, 255, 255, 0.1);
              padding: 15px;
              border-radius: 8px;
              backdrop-filter: blur(10px);
            }
            
            .header-item h3 {
              margin: 0 0 5px 0;
              font-size: 0.875rem;
              font-weight: 500;
              opacity: 0.9;
              text-transform: uppercase;
              letter-spacing: 0.05em;
            }
            
            .header-item p {
              margin: 0;
              font-size: 1.125rem;
              font-weight: 600;
            }
            
            .content {
              padding: 30px;
            }
            
            .product-card {
              background: white;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              margin-bottom: 30px;
              overflow: hidden;
              box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
            }
            
            .product-header {
              background: #f8fafc;
              padding: 25px;
              border-bottom: 1px solid #e2e8f0;
              display: flex;
              align-items: center;
              gap: 25px;
            }
            
            .product-image {
              width: 120px;
              height: 120px;
              object-fit: cover;
              border-radius: 8px;
              border: 2px solid #e2e8f0;
            }
            
            .product-info {
              flex: 1;
            }
            
            .product-code {
              font-size: 2rem;
              font-weight: 700;
              color: #1e293b;
              margin: 0 0 10px 0;
              letter-spacing: -0.025em;
            }
            
            .product-description {
              font-size: 1.25rem;
              color: #475569;
              margin: 0 0 15px 0;
              font-weight: 500;
            }
            
            .product-color {
              display: flex;
              align-items: center;
              gap: 10px;
            }
            
            .color-swatch {
              width: 24px;
              height: 24px;
              border-radius: 50%;
              border: 2px solid #e2e8f0;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
              display: inline-block;
              vertical-align: middle;
            }
            
            .color-name {
              font-size: 1rem;
              color: #64748b;
              font-weight: 500;
            }
            
            .table-container {
              overflow-x: auto;
              margin: 0;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 0;
            }
            
            th {
              background: #f1f5f9;
              color: #475569;
              font-weight: 600;
              text-transform: uppercase;
              font-size: 0.75rem;
              letter-spacing: 0.05em;
              padding: 15px 12px;
              text-align: left;
              border-bottom: 2px solid #e2e8f0;
            }
            
            td {
              padding: 15px 12px;
              border-bottom: 1px solid #f1f5f9;
              vertical-align: middle;
            }
            
            .status-badge {
              display: inline-flex;
              align-items: center;
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 0.75rem;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.05em;
            }
            
            .status-pending {
              background: #fef3c7;
              color: #92400e;
            }
            
            .status-prepared {
              background: #d1fae5;
              color: #065f46;
            }
            
            .quantity-input {
              width: 60px;
              height: 40px;
              border: 2px solid #d1d5db;
              border-radius: 6px;
              text-align: center;
              font-size: 1rem;
              font-weight: 600;
              color: #374151;
              background: #f9fafb;
            }
            
            .notes-cell {
              max-width: 200px;
            }
            
            .notes-text {
              font-size: 0.875rem;
              color: #6b7280;
              font-style: italic;
            }
            
            .footer {
              background: #f8fafc;
              padding: 25px;
              border-top: 1px solid #e2e8f0;
              text-align: center;
            }
            
            .print-button {
              background: #3b82f6;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 1rem;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
            }
            
            .print-button:hover {
              background: #2563eb;
              transform: translateY(-1px);
            }
            
            @media print {
              body { background: white; }
              .print-container { box-shadow: none; }
              .print-button { display: none; }
              .product-card { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="header">
              <h1>Preparación de Pedido #${order.number}</h1>
              <div class="header-info">
                <div class="header-item">
                  <h3>Cliente</h3>
                  <p>${order.customer?.name || 'N/A'}</p>
                </div>
                <div class="header-item">
                  <h3>Fecha del Pedido</h3>
                  <p>${new Date(order.date).toLocaleDateString('es-ES', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</p>
                </div>
                <div class="header-item">
                  <h3>Total del Pedido</h3>
                  <p>€${order.total?.toFixed(2) || '0.00'}</p>
                </div>
                <div class="header-item">
                  <h3>Fecha de Impresión</h3>
                  <p>${new Date().toLocaleDateString('es-ES', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</p>
                </div>
              </div>
            </div>
            
            <div class="content">
              ${groupItemsByProduct().map(group => `
                <div class="product-card">
                  <div class="product-header">
                    ${group.productImage ? 
                      `<img src="${group.productImage}" alt="${group.productDescription}" class="product-image">` : 
                      `<div class="product-image" style="background: #e2e8f0; display: flex; align-items: center; justify-content: center; color: #64748b; font-size: 0.875rem; text-align: center;">Sin imagen</div>`
                    }
                    <div class="product-info">
                      <h2 class="product-code">${group.productCode}</h2>
                      <p class="product-description">${group.productDescription}</p>
                      <div class="product-color">
                        <div class="color-swatch" style="background-color: ${getColorHex(group.color)}; border: 2px solid #e2e8f0;" title="Color: ${getColorDescription(group.color)} (${getColorHex(group.color)})"></div>
                        <span class="color-name">${getColorDescription(group.color)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Estado</th>
                          <th>Talla</th>
                          <th>Stock</th>
                          <th>Pendiente</th>
                          <th>Ya Preparado</th>
                          <th>A Preparar</th>
                          <th>Notas</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${group.items.map(item => {
                          const stockQty = stockAvailable[item.id] || 0;
                          const isCompleted = (item.preparation_quantity || 0) >= item.pending_quantity;
                          const hasPreparation = (item.preparation_quantity || 0) > 0;
                          
                          return `
                            <tr>
                              <td>
                                <span class="status-badge ${isCompleted ? 'status-prepared' : 'status-pending'}">
                                  ${isCompleted ? 'Completado' : 'Pendiente'}
                                </span>
                              </td>
                              <td style="font-weight: 600; font-size: 1.1rem;">${item.size}</td>
                              <td style="font-weight: 600; color: ${stockQty > 0 ? '#059669' : '#dc2626'};">
                                ${stockQty}
                              </td>
                              <td style="font-weight: 600;">${item.pending_quantity}</td>
                              <td style="font-weight: 600; color: #059669;">${item.preparation_quantity || 0}</td>
                              <td>
                                <input type="number" class="quantity-input" value="${quantities[item.id] || 0}" readonly>
                              </td>
                              <td class="notes-cell">
                                <span class="notes-text">${notes[item.id] || ''}</span>
                              </td>
                            </tr>
                          `;
                        }).join('')}
                      </tbody>
                    </table>
                  </div>
                </div>
              `).join('')}
            </div>
            
            <div class="footer">
              <button class="print-button" onclick="window.print()">
                🖨️ Imprimir Preparación
              </button>
            </div>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Función auxiliar para obtener el color hexadecimal usando el contexto
  const getColorHex = (colorName) => {
    if (!colorName) return '#C0C0C0';
    
    // Primero intentar usar el contexto de colores
    if (colorSettings && colorSettings[colorName]) {
      const colorData = colorSettings[colorName];
      const hexColor = typeof colorData === 'string' ? colorData : colorData.hex;
      
      return hexColor || '#C0C0C0';
    }
    
    // Fallback: mapeo manual de colores comunes
    const colorMap = {
      'negro': '#000000',
      'blanco': '#FFFFFF',
      'rojo': '#FF0000',
      'azul': '#0000FF',
      'verde': '#008000',
      'amarillo': '#FFFF00',
      'gris': '#808080',
      'marron': '#8B4513',
      'rosa': '#FFC0CB',
      'morado': '#800080',
      'naranja': '#FFA500',
      'kaki': '#C3B091',
      'beige': '#F5F5DC',
      'crema': '#FFFDD0',
      'azul marino': '#000080',
      'verde oliva': '#808000',
      'rojo oscuro': '#8B0000',
      'azul claro': '#87CEEB',
      'verde claro': '#90EE90',
      'gris claro': '#D3D3D3',
      'gris oscuro': '#404040',
      // Variaciones comunes
      'caqui': '#C3B091',
      'khaki': '#C3B091',
      'café': '#8B4513',
      'cafe': '#8B4513',
      'brown': '#8B4513',
      'black': '#000000',
      'white': '#FFFFFF',
      'red': '#FF0000',
      'blue': '#0000FF',
      'green': '#008000',
      'yellow': '#FFFF00',
      'gray': '#808080',
      'grey': '#808080',
      'pink': '#FFC0CB',
      'purple': '#800080',
      'orange': '#FFA500'
    };
    
    const normalizedColor = colorName.toLowerCase().trim();
    const hexColor = colorMap[normalizedColor];
    

    
    return hexColor || '#C0C0C0';
  };

  // Función auxiliar para obtener la descripción del color
  const getColorDescription = (colorName) => {
    if (!colorName) return 'Sin color';
    
    // Usar el contexto de colores si está disponible
    if (colorSettings && colorSettings[colorName]) {
      const colorData = colorSettings[colorName];
      const description = typeof colorData === 'string' ? colorName : (colorData.description || colorName);
      return description;
    }
    
    // Fallback: usar el nombre del color tal como viene
    return colorName;
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  const groupedItems = groupItemsByProduct();
  const hasStockIssues = order.order_items.some(item => {
    const stockQty = stockAvailable[item.id] || 0;
    return stockQty < item.pending_quantity;
  });

  const totals = calculateTotals();

  return (
    <div className="space-y-4 max-h-[85vh] flex flex-col bg-white p-4 w-full">
      {/* Cabecera rediseñada (sticky) */}
      <div className="flex flex-col gap-1 border-b pb-2 mb-2 sticky top-0 bg-white z-20">
        {/* Fila 1: Título, total y acciones */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-lg font-semibold mb-0.5">Preparar Pedido</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs text-gray-500 font-medium">Total</div>
              <div className="text-xl font-bold text-gray-800">${order.total?.toFixed(2)}</div>
            </div>
            <Button 
              variant="outline" 
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handlePrepare}
              disabled={preparing || !Object.values(quantities).some(qty => qty > 0)}
              className="bg-gray-600 hover:bg-gray-700 text-white"
            >
              {preparing ? 'Preparando...' : 'Preparar Pedido'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="flex items-center gap-2"
            >
              <Printer className="h-4 w-4" />
              Imprimir
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSummary(prev => !prev)}
              className="h-8 px-2"
              title={showSummary ? 'Ocultar resumen' : 'Mostrar resumen'}
            >
              {showSummary ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        {showSummary && (
          <>
            {/* Fila 2: Datos cliente y resumen totales */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mt-1">
              {/* Datos cliente */}
              <div className="flex flex-col gap-0.5 min-w-[160px]">
                <div className="text-xs text-gray-500 font-medium">Cliente</div>
                <div className="text-sm font-semibold text-gray-800 leading-tight">{order.customer?.name}</div>
                <div className="text-[11px] text-gray-500">Fecha: {new Date(order.date).toLocaleDateString()}</div>
              </div>
              {/* Totales */}
              <div className="flex-1 flex flex-row justify-center gap-4 mt-1 md:mt-0">
                <div className="bg-white shadow rounded-md px-4 py-2 flex flex-col items-center min-w-[90px] border border-orange-100">
                  <div className="text-2xl font-extrabold text-orange-500">{totals.totalPending}</div>
                  <div className="uppercase text-[10px] text-gray-400 font-semibold tracking-wider mt-0.5">Pendientes</div>
                </div>
                <div className="bg-white shadow rounded-md px-4 py-2 flex flex-col items-center min-w-[90px] border border-blue-100">
                  <div className="text-2xl font-extrabold text-blue-500">{totals.totalToPrepare}</div>
                  <div className="uppercase text-[10px] text-gray-400 font-semibold tracking-wider mt-0.5">A preparar</div>
                </div>
                <div className="bg-white shadow rounded-md px-4 py-2 flex flex-col items-center min-w-[90px] border border-green-100">
                  <div className="text-2xl font-extrabold text-green-500">{totals.totalPrepared}</div>
                  <div className="uppercase text-[10px] text-gray-400 font-semibold tracking-wider mt-0.5">Ya preparadas</div>
                </div>
              </div>
            </div>
            {/* Barra de progreso */}
            <div className="w-full max-w-xl mx-auto mt-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2 relative overflow-hidden">
                  {(() => {
                    const totalItems = totals.totalPending + totals.totalPrepared;
                    const progressPercentage = totalItems > 0 ? (totals.totalPrepared / totalItems) * 100 : 0;
                    return (
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(100, progressPercentage)}%` }}
                      />
                    );
                  })()}
                </div>
                <div className="w-10 text-center text-xs font-semibold text-gray-700">
                  {(() => {
                    const totalItems = totals.totalPending + totals.totalPrepared;
                    const progressPercentage = totalItems > 0 ? (totals.totalPrepared / totalItems) * 100 : 0;
                    return `${Math.round(progressPercentage)}%`;
                  })()}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {hasStockIssues && (
        <Alert variant="warning" className="bg-yellow-50 border-yellow-200">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-600">Atención</AlertTitle>
          <AlertDescription className="text-yellow-700">
            Algunas líneas tienen stock insuficiente para la cantidad pendiente
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2 min-w-[220px]">
          <Switch
            id="show-pending"
            checked={showOnlyPending}
            onCheckedChange={setShowOnlyPending}
          />
          <Label htmlFor="show-pending" className="text-sm">
            {showOnlyPending ? (
              <div className="flex items-center gap-1">
                <EyeOff className="h-4 w-4" />
                Solo pendientes
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                Mostrar todo
              </div>
            )}
          </Label>
        </div>
        <div className="flex-1 mx-4 max-w-[540px]">
          <div className="relative">
            <Input
              placeholder="Filtrar por código de artículo..."
              value={codeFilter}
              onChange={(e) => setCodeFilter(e.target.value)}
              className="w-full pr-8"
            />
            {codeFilter && (
              <button
                type="button"
                onClick={() => setCodeFilter('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Limpiar filtro"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePrepareAll}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Check className="h-4 w-4" />
            Preparar Todo según Stock
          </Button>
          <Button
            onClick={handleResetInputs}
            variant="ghost"
            className="flex items-center gap-2"
            disabled={preparing}
            title="Reinicia los campos 'A Preparar'"
          >
            <RotateCcw className="h-4 w-4" />
            Reset A Preparar
          </Button>
          <Button
            onClick={handleResetPreparedAll}
            variant="outline"
            className="flex items-center gap-2 text-red-600 border-red-200 hover:text-red-700"
            disabled={preparing}
            title="Deshace toda la preparación ya guardada"
          >
            <RotateCcw className="h-4 w-4" />
            Reset Ya Preparado
          </Button>
        </div>
      </div>

      <div className="overflow-auto flex-1">
        {groupedItems.map((group) => (
          <div key={`${group.productCode}-${group.color}`} className="mb-8">
            <div className="mb-4 flex items-start gap-4">
              {group.productImage && (
                <img 
                  src={group.productImage} 
                  alt={group.productCode}
                  className="w-16 h-16 object-cover rounded"
                />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-medium">{group.productCode}</h3>
                  <ColorBadge color={group.color} />
                </div>
                <p className="text-sm text-gray-600">{group.productDescription}</p>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Estado</TableHead>
                  <TableHead>Talla</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Pendiente</TableHead>
                  <TableHead>Ya Preparado</TableHead>
                  <TableHead>A Preparar</TableHead>
                  <TableHead>Notas</TableHead>
                  <TableHead className="w-[100px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {group.items.map((item) => {
                  const stockQty = stockAvailable[item.id] || 0;
                  const hasStockIssue = stockQty < item.pending_quantity;
                  const isCompleted = item.pending_quantity === 0;
                  const hasPreparation = item.preparation_quantity > 0;

                  return (
                    <TableRow 
                      key={item.id}
                      className={isCompleted ? "bg-gray-50" : undefined}
                    >
                      <TableCell>
                        {isCompleted ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Completado
                          </span>
                        ) : hasPreparation ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Parcial
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Pendiente
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{item.size}</TableCell>
                      <TableCell className={hasStockIssue ? "text-red-600" : ""}>
                        {stockQty}
                      </TableCell>
                      <TableCell>{item.pending_quantity}</TableCell>
                      <TableCell>{item.preparation_quantity || 0}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          max={Math.min(item.pending_quantity, stockQty)}
                          value={quantities[item.id] || 0}
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          className="w-20"
                          disabled={item.pending_quantity === 0}
                        />
                      </TableCell>
                      <TableCell>
                        <Textarea
                          value={notes[item.id] || ''}
                          onChange={(e) => handleNoteChange(item.id, e.target.value)}
                          placeholder="Notas..."
                          className="w-full h-8 min-h-0 py-1"
                          disabled={item.pending_quantity === 0}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {!isCompleted && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => handlePrepareItem(item.id)}
                              title="Preparar cantidad máxima según stock"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          )}
                          {hasPreparation && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-yellow-600 hover:text-yellow-700"
                              onClick={() => handleUnprepare(item.id)}
                              title="Despreparar línea"
                              disabled={preparing}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ))}
      </div>

      {/* Botonera inferior eliminada para ganar espacio vertical; acciones están en cabecera */}
    </div>
  );
} 